import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErrorMessage, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, ArrowsDownUp } from "@phosphor-icons/react";

export default function Inventory() {
    const [searchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [ledger, setLedger] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ product_id: "", quantity: 0, movement_type: "in", remarks: "" });

    useEffect(() => {
        // Refill deep-link support: ?product=<id>&type=in
        const pid = searchParams.get("product");
        const type = searchParams.get("type") || "in";
        if (pid) setForm((f) => ({ ...f, product_id: pid, movement_type: type }));
        if (pid) setDialogOpen(true);
    }, [searchParams]);

    const load = async () => {
        setLoading(true);
        try {
            const [p, l] = await Promise.all([
                api.get("/products", { params: { limit: 500 } }),
                api.get("/inventory/ledger", { params: { limit: 200 } }),
            ]);
            setProducts(p.data);
            setLedger(l.data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submitMove = async () => {
        if (!form.product_id) return toast.error("Select a product");
        if (Number(form.quantity) <= 0 && form.movement_type !== "adjustment") return toast.error("Quantity must be > 0");
        try {
            await api.post("/inventory/move", {
                product_id: form.product_id,
                quantity: Number(form.quantity),
                movement_type: form.movement_type,
                remarks: form.remarks,
            });
            toast.success("Stock updated");
            setDialogOpen(false);
            setForm({ product_id: "", quantity: 0, movement_type: "in", remarks: "" });
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    return (
        <div className="space-y-6" data-testid="inventory-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Stock</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Inventory</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">Manual stock movements & permanent ledger.</p>
                </div>
                <Button onClick={() => setDialogOpen(true)} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="inventory-move-btn">
                    <ArrowsDownUp size={16} weight="bold" className="mr-1.5" /> New Movement
                </Button>
            </div>

            <Tabs defaultValue="ledger">
                <TabsList>
                    <TabsTrigger value="ledger" data-testid="tab-ledger">Stock Ledger</TabsTrigger>
                    <TabsTrigger value="stock" data-testid="tab-stock">Current Stock</TabsTrigger>
                </TabsList>
                <TabsContent value="ledger">
                    <div className="bg-white border border-neutral-200 rounded-md overflow-hidden mt-4" data-testid="ledger-table">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-semibold">Date</th>
                                        <th className="text-left px-5 py-3 font-semibold">Product</th>
                                        <th className="text-left px-5 py-3 font-semibold">Type</th>
                                        <th className="text-right px-5 py-3 font-semibold">Qty</th>
                                        <th className="text-left px-5 py-3 font-semibold">Reference</th>
                                        <th className="text-left px-5 py-3 font-semibold">User</th>
                                        <th className="text-left px-5 py-3 font-semibold">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && <tr><td colSpan={7} className="text-center py-10 text-neutral-400">Loading...</td></tr>}
                                    {!loading && ledger.length === 0 && (
                                        <tr><td colSpan={7} className="text-center py-10 text-neutral-400">No stock movements yet.</td></tr>
                                    )}
                                    {ledger.map((l) => (
                                        <tr key={l.id} className="data-row border-t border-neutral-100">
                                            <td className="px-5 py-2.5 text-neutral-500 whitespace-nowrap">{formatDate(l.timestamp)}</td>
                                            <td className="px-5 py-2.5 font-medium">{l.product_name}</td>
                                            <td className="px-5 py-2.5"><MoveBadge type={l.movement_type} /></td>
                                            <td className="px-5 py-2.5 text-right font-bold tabular" style={{ color: l.quantity < 0 ? "#DC2626" : "#15803D" }}>
                                                {l.quantity > 0 ? "+" : ""}{l.quantity}
                                            </td>
                                            <td className="px-5 py-2.5 text-neutral-500 text-xs uppercase tracking-wider">{l.reference_type}</td>
                                            <td className="px-5 py-2.5 text-neutral-500">{l.user_name}</td>
                                            <td className="px-5 py-2.5 text-neutral-500 max-w-[240px] truncate">{l.remarks}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="stock">
                    <div className="bg-white border border-neutral-200 rounded-md overflow-hidden mt-4" data-testid="stock-table">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-semibold">Product</th>
                                        <th className="text-left px-5 py-3 font-semibold">SKU</th>
                                        <th className="text-right px-5 py-3 font-semibold">Current</th>
                                        <th className="text-right px-5 py-3 font-semibold">Min</th>
                                        <th className="text-right px-5 py-3 font-semibold">Max</th>
                                        <th className="text-left px-5 py-3 font-semibold">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p) => {
                                        const cs = Number(p.current_stock || 0);
                                        const mn = Number(p.min_stock || 0);
                                        const status = cs <= 0 ? "Out" : cs <= mn ? "Low" : "OK";
                                        const bg = { Out: "#FEE2E2", Low: "#FEF3C7", OK: "#DCFCE7" }[status];
                                        const fg = { Out: "#DC2626", Low: "#B45309", OK: "#15803D" }[status];
                                        return (
                                            <tr key={p.id} className="data-row border-t border-neutral-100">
                                                <td className="px-5 py-2.5 font-medium">{p.name}</td>
                                                <td className="px-5 py-2.5 font-mono text-xs text-neutral-500">{p.sku}</td>
                                                <td className="px-5 py-2.5 text-right tabular font-bold">{cs}</td>
                                                <td className="px-5 py-2.5 text-right tabular text-neutral-500">{mn}</td>
                                                <td className="px-5 py-2.5 text-right tabular text-neutral-500">{p.max_stock}</td>
                                                <td className="px-5 py-2.5">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: bg, color: fg }}>{status}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="move-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display">New Stock Movement</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Product</Label>
                            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="move-form-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                                <SelectContent>
                                    {products.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>{p.name} · <span className="text-xs opacity-60 font-mono">{p.sku}</span></SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Type</Label>
                                <Select value={form.movement_type} onValueChange={(v) => setForm({ ...form, movement_type: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="move-form-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="in">Stock In</SelectItem>
                                        <SelectItem value="out">Stock Out</SelectItem>
                                        <SelectItem value="adjustment">Adjustment (set exact)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">
                                    Quantity {form.movement_type === "adjustment" && "(target value)"}
                                </Label>
                                <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1.5" data-testid="move-form-quantity" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Remarks</Label>
                            <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} className="mt-1.5" data-testid="move-form-remarks" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="move-form-cancel">Cancel</Button>
                        <Button onClick={submitMove} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="move-form-submit">Record Movement</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function MoveBadge({ type }) {
    const map = {
        in:  { bg: "#DCFCE7", fg: "#15803D", label: "Stock In" },
        out: { bg: "#FEE2E2", fg: "#DC2626", label: "Stock Out" },
        adjustment: { bg: "#FEF3C7", fg: "#B45309", label: "Adjustment" },
    };
    const s = map[type] || { bg: "#F3F4F6", fg: "#374151", label: type };
    return (
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: s.bg, color: s.fg }}>
            {s.label}
        </span>
    );
}
